import clsx from 'clsx';
import { useMemo, useState } from 'react';

import { ExploreChallenges } from './ExploreChallenges';
import { SizeScale, SubdivisionSquare } from './MaterialsChemistryScaleVisuals';
import {
  DEFAULT_NANO_SETUP,
  NANO_CHALLENGES,
  NANO_QUESTION,
  calculateNanoExplore,
  nanoExploreFields,
  type NanoExploreSetup,
  type NanoShape,
} from './materialsNanoExplore';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { formatSig } from './gasLaws';

const SHAPES: { id: NanoShape; label: string }[] = [
  { id: 'cube', label: 'Cube' },
  { id: 'sphere', label: 'Sphere' },
];

/**
 * Explore view for the Nanomaterials mode: typed size and cut size drive the worked steps, the
 * size scale and the cut face from one state. The practice cases below stay unchanged.
 */
export function MaterialsNanoExplorer() {
  const [setup, setSetup] = useState<NanoExploreSetup>(DEFAULT_NANO_SETUP);
  const result = useMemo(() => calculateNanoExplore(setup), [setup]);
  const fields = nanoExploreFields(setup.shape);
  const values = { sizeNm: setup.sizeNm, cutNm: setup.cutNm };

  return (
    <section
      aria-label="Nanoscale explorer"
      className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6"
    >
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Explore</p>
      <h3 className="mt-1 text-xl font-extrabold text-ink">Change the size, watch the surface grow</h3>
      <div className="mt-4 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-4">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Particle shape">
            {SHAPES.map((shape) => (
              <button
                key={shape.id}
                type="button"
                aria-pressed={setup.shape === shape.id}
                onClick={() => setSetup((current) => ({ ...current, shape: shape.id }))}
                className={clsx(
                  'rounded-full border px-4 py-2 text-sm font-extrabold',
                  setup.shape === shape.id ? 'border-ink bg-ink text-white' : 'border-hairline bg-white text-ink',
                )}
              >
                {shape.label}
              </button>
            ))}
          </div>
          <ModelInputPanel
            key={setup.shape}
            legend="Particle and cut size"
            fields={fields}
            values={values}
            onChange={(next) => setSetup((current) => ({ ...current, ...next }))}
          />
          <ExploreChallenges question={NANO_QUESTION} challenges={NANO_CHALLENGES} result={{ setup, result }} />
          <WorkedStepsPanel
            title="Nanoscale working"
            steps={result.steps}
            stepTestIdPrefix="nano-step"
            resultTestId="nano-result"
            result={`SA:V = ${formatSig(result.ratioPerNm, 3)} nm⁻¹ · ${result.sizeClassLabel}`}
            limitNote={result.limitNote}
          />
        </div>
        <div className="min-w-0 space-y-4">
          <SizeScale
            sizeNm={result.scaleSizeNm}
            label={setup.shape === 'cube' ? 'Your cube' : 'Your sphere'}
            revealed
          />
          {result.perEdge !== null ? (
            <SubdivisionSquare
              perEdge={result.perEdge}
              caption={`Each edge cut into ${formatSig(result.perEdge, 3)} pieces${result.perEdge > 10 ? ' (drawn as 10)' : ''}`}
            />
          ) : (
            <p className="rounded-2xl bg-surface p-4 text-sm font-bold text-ink-soft">
              {setup.shape === 'cube' ? 'No valid cut to draw yet.' : 'Cutting applies to the cube.'}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
